import { useEffect, useRef, useState } from "react";
import { Calendar, CaretDown, CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * Single-date picker with the same visual language as the premium
 * DateRangeFilter (month + year dropdowns, day grid, blue-selected).
 *
 * Props
 *   value    — ISO YYYY-MM-DD string (or "")
 *   onChange — (iso) => void, fires on day click with "YYYY-MM-DD"
 *   label    — small overline above the field (optional)
 *   testid   — data-testid base string
 *   minDate  — optional ISO string to disable earlier dates
 *   maxDate  — optional ISO string to disable later dates
 */
export default function PremiumDatePicker({
  value = "",
  onChange,
  label,
  testid = "premium-date",
  minDate,
  maxDate,
  placeholder = "Pick date",
  className = "",
}) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selected = parseISO(value);
  const [viewMonth, setViewMonth] = useState(() => {
    const base = selected || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const pickDay = (d) => {
    onChange?.(toIso(d));
    setOpen(false);
  };

  const display = selected
    ? selected.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
    : placeholder;

  const minD = parseISO(minDate);
  const maxD = parseISO(maxDate);

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && (
        <div className="text-[11px] uppercase tracking-[0.15em] text-[#9CA3AF] font-semibold mb-1">
          {label}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className={`h-10 px-3 rounded-lg border text-[13px] font-medium flex items-center gap-2 min-w-[170px] transition-colors ${
          selected
            ? "bg-white border-[#E5E7EB] text-[#121216] hover:border-[#121216]"
            : "bg-white border-[#E5E7EB] text-[#9CA3AF] hover:border-[#121216] hover:text-[#121216]"
        }`}
      >
        <Calendar size={14} weight="bold" className="opacity-70" />
        <span className="tabular-nums">{display}</span>
        <CaretDown size={10} weight="bold" className="opacity-50 ml-auto" />
      </button>

      {open && (
        <div
          className="absolute left-0 mt-2 w-[300px] bg-white rounded-2xl border border-[#E5E7EB] z-40 overflow-hidden p-4"
          style={{ boxShadow: "0 20px 48px rgba(30, 95, 235, 0.15)" }}
          data-testid={`${testid}-popover`}
        >
          {/* Month / Year navigator */}
          <div className="flex items-center justify-between px-1 mb-3">
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              data-testid={`${testid}-prev`}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <div className="flex items-center gap-1.5">
              <select
                value={viewMonth.getMonth()}
                onChange={(e) => setViewMonth(new Date(viewMonth.getFullYear(), parseInt(e.target.value, 10), 1))}
                data-testid={`${testid}-month`}
                className="h-8 pr-2 pl-2 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-slate-700 focus:border-[#1E5FEB] focus:outline-none"
              >
                {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
              <select
                value={viewMonth.getFullYear()}
                onChange={(e) => setViewMonth(new Date(parseInt(e.target.value, 10), viewMonth.getMonth(), 1))}
                data-testid={`${testid}-year`}
                className="h-8 pr-2 pl-2 rounded-lg border border-[#E5E7EB] bg-white text-[12px] font-semibold text-slate-700 focus:border-[#1E5FEB] focus:outline-none"
              >
                {Array.from({ length: 40 }, (_, k) => new Date().getFullYear() + 2 - k).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              data-testid={`${testid}-next`}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {["Mo","Tu","We","Th","Fr","Sa","Su"].map((d) => (
              <div key={d} className="text-[10px] font-bold text-slate-400 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {buildMonthDays(viewMonth).map((d, i) => {
              if (!d) return <div key={i} />;
              const isSel = sameDay(d, selected);
              const isToday = sameDay(d, new Date());
              const disabled = (minD && d < stripTime(minD)) || (maxD && d > stripTime(maxD));
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !disabled && pickDay(d)}
                  disabled={disabled}
                  data-testid={`${testid}-day-${toIso(d)}`}
                  className={`h-9 rounded-md text-[13px] font-mono tabular-nums transition-all ${
                    disabled
                      ? "text-slate-300 cursor-not-allowed"
                      : isSel
                        ? "bg-[#1E5FEB] text-white font-bold shadow-sm"
                        : isToday
                          ? "bg-slate-100 text-[#1E5FEB] font-bold ring-1 ring-[#1E5FEB]/30"
                          : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => pickDay(new Date())}
              data-testid={`${testid}-today`}
              className="h-8 px-3 text-[11px] font-semibold text-[#1E5FEB] hover:bg-blue-50 rounded-lg"
            >
              Today
            </button>
            {selected && (
              <button
                type="button"
                onClick={() => { onChange?.(""); setOpen(false); }}
                data-testid={`${testid}-clear`}
                className="h-8 px-3 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 rounded-lg"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map((v) => parseInt(v, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function sameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function stripTime(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Returns a 6-row array: null pads for leading/trailing blanks. Week starts Mon. */
function buildMonthDays(viewMonth) {
  const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const last = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  const leadingPad = (first.getDay() + 6) % 7;
  const total = leadingPad + last.getDate();
  return Array.from({ length: Math.ceil(total / 7) * 7 }, (_, i) => {
    const dayNum = i - leadingPad + 1;
    if (dayNum < 1 || dayNum > last.getDate()) return null;
    return new Date(viewMonth.getFullYear(), viewMonth.getMonth(), dayNum);
  });
}
